import {StoppageInfo} from '@domain/stoppages'
import {AnalysisListPage, CreateAnalysisPage, AnalysisDetailPage} from '@features/rcfa-list'
import type {NavigateToPlantOptions, RcfaCreatePlantOption} from '@features/rcfa-list'
import {StoppageList, StoppageNavigationProvider} from '@features/stoppage-list'
import {useCallback} from 'react'
import {ErrorBoundary} from 'react-error-boundary'
import {Outlet, useLocation, Navigate, useNavigate} from 'react-router'

import {useUrlParam} from '../shared/hooks/useUrlParam'

import {RcfaErrorFallback} from './container/RcfaErrorFallback'
import {TasksList} from './pages/TasksList'
import {REL_ROUTES, routes} from './routes'

const MaintainStoppageList = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const plantId = useUrlParam('plantId')

  const navigateToAnalysis = useCallback(
    async (stoppage: StoppageInfo) => {
      await navigate(`/plant/${plantId}/rcfa/graphDetail/${stoppage.analysisId}/graph`, {
        state: location
      })
    },
    [navigate, location, plantId]
  )

  const navigateToCreateAnalysis = useCallback(
    async (stoppage: StoppageInfo) => {
      // Use the stoppage's own plantId so that cross-plant stoppages (e.g. Gladstone
      // viewed while browsing plant 0014) open the create form for the correct plant.
      const targetPlantId = stoppage.plantId ?? plantId
      await navigate(routes.newAnalysis.replace(':plantId', targetPlantId) + '?type=stoppage', {
        state: {selectedStoppage: stoppage}
      })
    },
    [navigate, plantId]
  )

  return (
    <StoppageNavigationProvider
      navigateToAnalysis={navigateToAnalysis}
      navigateToCreateAnalysis={navigateToCreateAnalysis}
    >
      <StoppageList inverted />
    </StoppageNavigationProvider>
  )
}

const MaintainCreateAnalysisPage = () => {
  const navigate = useNavigate()
  const plantId = useUrlParam('plantId')
  const location = useLocation()

  const navigateToStoppageList = useCallback(async () => {
    await navigate(routes.stoppageList.replace(':plantId', plantId))
  }, [navigate, plantId])

  const navigateToPlant = useCallback(
    async (
      plant: RcfaCreatePlantOption,
      {preservedDescription, searchParams}: NavigateToPlantOptions
    ) => {
      const search = searchParams.toString()

      await navigate(
        `${routes.newAnalysis.replace(':plantId', plant.plantId)}${search ? `?${search}` : ''}`,
        {
          state: {
            ...location.state,
            preservedDescription
          }
        }
      )
    },
    [location.state, navigate]
  )

  return (
    <CreateAnalysisPage
      onGoToStoppageList={navigateToStoppageList}
      onNavigateToPlant={navigateToPlant}
    />
  )
}

export const RcfaRoutes = [
  {
    path: REL_ROUTES.graphDetail,
    element: <AnalysisDetailPage />
  },
  {
    path: REL_ROUTES.newAnalysis,
    element: <MaintainCreateAnalysisPage />
  },
  {
    path: REL_ROUTES.analysisList,
    element: <AnalysisListPage inverted />
  },
  {
    path: REL_ROUTES.tasksList,
    element: <TasksList />
  },
  {
    path: REL_ROUTES.stoppageList,
    element: <MaintainStoppageList />
  },
  {
    path: '*',
    element: <Navigate to={REL_ROUTES.analysisList} replace />
  }
]
export const RcfaApp = () => {
  const location = useLocation()
  return (
    <ErrorBoundary FallbackComponent={RcfaErrorFallback} resetKeys={[location.pathname]}>
      <Outlet />
    </ErrorBoundary>
  )
}
